import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  createContext,
} from 'react'

export interface Widget {
  i: string
  x: number
  y: number
  w: number
  h: number
  type: 'chart' | 'map' | 'text' | 'table'
  config?: {
    overlay?: string
    pois?: string[]
    prompt?: string
  }
}

export interface Workspace {
  id: string
  name: string
  description: string
  pinned: boolean
  favorited: boolean
  updatedAt: string
  widgets: Widget[]
  counter: number
}

const INITIAL_WIDGETS_1: Widget[] = [
  {
    i: 'map-1',
    x: 0,
    y: 0,
    w: 8,
    h: 6,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa',
      pois: ['wind_farm'],
    },
  },
  {
    i: 'text-1',
    x: 8,
    y: 0,
    w: 4,
    h: 6,
    type: 'text',
    config: {
      prompt:
        'Give me the wind forecast with Model divergences between EPT2HRRR, AIFS and IFS for the next 5 days',
    },
  },
]

const INITIAL_WIDGETS_2: Widget[] = [
  { i: 'table-1', x: 0, y: 0, w: 6, h: 5, type: 'table' },
  { i: 'chart-2', x: 6, y: 0, w: 6, h: 5, type: 'chart' },
  { i: 'map-2', x: 0, y: 5, w: 12, h: 6, type: 'map' },
]

function createSeedWorkspaces(): Workspace[] {
  const now = new Date().toISOString()
  return [
    {
      id: '1',
      name: 'UK Wind Forecast Analysis',
      description: 'Multi-model wind speed comparison for UK regions',
      pinned: true,
      favorited: true,
      updatedAt: now,
      widgets: INITIAL_WIDGETS_1,
      counter: 2,
    },
    {
      id: '2',
      name: 'Nordic Energy Trading 7D',
      description: '7-day ahead energy price forecasting for Nordic markets',
      pinned: true,
      favorited: false,
      updatedAt: now,
      widgets: INITIAL_WIDGETS_2,
      counter: 3,
    },
    {
      id: '3',
      name: 'European Pressure Systems',
      description: 'Synoptic pressure analysis across European regions',
      pinned: false,
      favorited: true,
      updatedAt: now,
      widgets: [],
      counter: 0,
    },
    {
      id: '4',
      name: 'Solar Radiation Q3 Outlook',
      description: 'Solar energy generation potential for Q3 2026',
      pinned: false,
      favorited: false,
      updatedAt: now,
      widgets: [],
      counter: 0,
    },
    {
      id: '5',
      name: 'ENTSOE Market Dashboard',
      description: 'European energy market prices, load, and generation',
      pinned: false,
      favorited: true,
      updatedAt: now,
      widgets: [],
      counter: 0,
    },
    {
      id: '6',
      name: 'North Sea Wind Comparison',
      description: 'EC AIFS vs IFS vs EPT-2 for offshore wind farms',
      pinned: false,
      favorited: false,
      updatedAt: now,
      widgets: [],
      counter: 0,
    },
    {
      id: '7',
      name: 'Station Benchmark Report',
      description: 'Forecast accuracy benchmarks against station observations',
      pinned: false,
      favorited: false,
      updatedAt: now,
      widgets: [],
      counter: 0,
    },
    {
      id: '8',
      name: 'Intraday Trading Signals',
      description: 'Short-term energy trading indicators and alerts',
      pinned: false,
      favorited: false,
      updatedAt: now,
      widgets: [],
      counter: 0,
    },
  ]
}

function loadWorkspaces(): Workspace[] {
  try {
    const stored = localStorage.getItem('jua-workspaces')
    if (stored) {
      const parsed = JSON.parse(stored)
      if (Array.isArray(parsed) && parsed.length > 0) return parsed
    }
  } catch (e) {
    console.error('Failed to parse workspaces', e)
  }
  return createSeedWorkspaces()
}

function loadActiveId(): string {
  return localStorage.getItem('jua-active-workspace') || '1'
}

interface WorkspacesContextValue {
  workspaces: Workspace[]
  activeWorkspaceId: string
  activeWorkspace: Workspace | null
  saveStatus: 'idle' | 'saving' | 'saved'
  setActiveWorkspace: (id: string) => void
  createWorkspace: () => string
  deleteWorkspace: (id: string) => void
  renameWorkspace: (id: string, newName: string) => void
  updateDescription: (id: string, newDesc: string) => void
  togglePin: (id: string) => void
  toggleFavorite: (id: string) => void
  updateWidgets: (widgets: Widget[], counter: number) => void
  updateWidgetsById: (id: string, widgets: Widget[], counter: number) => void
  reorderWorkspaces: (fromIndex: number, toIndex: number) => void
}

const WorkspacesContext = createContext<WorkspacesContextValue | null>(null)

export function WorkspacesProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>(loadWorkspaces)
  const [activeWorkspaceId, setActiveWorkspaceId] =
    useState<string>(loadActiveId)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>(
    'idle',
  )

  // Persist workspaces to localStorage
  useEffect(() => {
    setSaveStatus('saving')
    localStorage.setItem('jua-workspaces', JSON.stringify(workspaces))
    const timer = setTimeout(() => {
      setSaveStatus('saved')
    }, 400)
    const idleTimer = setTimeout(() => {
      setSaveStatus('idle')
    }, 2400)
    return () => {
      clearTimeout(timer)
      clearTimeout(idleTimer)
    }
  }, [workspaces])

  // Persist active workspace id
  useEffect(() => {
    localStorage.setItem('jua-active-workspace', activeWorkspaceId)
  }, [activeWorkspaceId])

  const activeWorkspace =
    workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0] || null

  const createWorkspace = useCallback(() => {
    const newId =
      Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
    const newWorkspace: Workspace = {
      id: newId,
      name: 'Untitled Workspace',
      description: 'Add a description...',
      pinned: false,
      favorited: false,
      updatedAt: new Date().toISOString(),
      widgets: [],
      counter: 0,
    }
    setWorkspaces((prev) => [newWorkspace, ...prev])
    setActiveWorkspaceId(newId)
    return newId
  }, [])

  const deleteWorkspace = useCallback(
    (id: string) => {
      setWorkspaces((prev) => {
        if (prev.length <= 1) return prev
        const filtered = prev.filter((w) => w.id !== id)
        if (activeWorkspaceId === id && filtered.length > 0) {
          setActiveWorkspaceId(filtered[0].id)
        }
        return filtered
      })
    },
    [activeWorkspaceId],
  )

  const renameWorkspace = useCallback((id: string, newName: string) => {
    setWorkspaces((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, name: newName, updatedAt: new Date().toISOString() }
          : w,
      ),
    )
  }, [])

  const updateDescription = useCallback((id: string, newDesc: string) => {
    setWorkspaces((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, description: newDesc, updatedAt: new Date().toISOString() }
          : w,
      ),
    )
  }, [])

  const togglePin = useCallback((id: string) => {
    setWorkspaces((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, pinned: !w.pinned, updatedAt: new Date().toISOString() }
          : w,
      ),
    )
  }, [])

  const toggleFavorite = useCallback((id: string) => {
    setWorkspaces((prev) =>
      prev.map((w) =>
        w.id === id
          ? {
              ...w,
              favorited: !w.favorited,
              updatedAt: new Date().toISOString(),
            }
          : w,
      ),
    )
  }, [])

  const updateWidgets = useCallback(
    (widgets: Widget[], counter: number) => {
      setWorkspaces((prev) =>
        prev.map((w) =>
          w.id === activeWorkspaceId
            ? { ...w, widgets, counter, updatedAt: new Date().toISOString() }
            : w,
        ),
      )
    },
    [activeWorkspaceId],
  )

  const updateWidgetsById = useCallback(
    (id: string, widgets: Widget[], counter: number) => {
      setWorkspaces((prev) =>
        prev.map((w) =>
          w.id === id
            ? { ...w, widgets, counter, updatedAt: new Date().toISOString() }
            : w,
        ),
      )
    },
    [],
  )

  const reorderWorkspaces = useCallback(
    (fromIndex: number, toIndex: number) => {
      setWorkspaces((prev) => {
        const updated = [...prev]
        const [moved] = updated.splice(fromIndex, 1)
        updated.splice(toIndex, 0, moved)
        return updated
      })
    },
    [],
  )

  const value: WorkspacesContextValue = {
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    saveStatus,
    setActiveWorkspace: setActiveWorkspaceId,
    createWorkspace,
    deleteWorkspace,
    renameWorkspace,
    updateDescription,
    togglePin,
    toggleFavorite,
    updateWidgets,
    updateWidgetsById,
    reorderWorkspaces,
  }

  return React.createElement(WorkspacesContext.Provider, { value }, children)
}

export function useWorkspaces(): WorkspacesContextValue {
  const ctx = useContext(WorkspacesContext)
  if (!ctx) {
    throw new Error('useWorkspaces must be used within a WorkspacesProvider')
  }
  return ctx
}
